import React, { useEffect, useLayoutEffect, useState } from 'react';
import styled from 'styled-components';
import { isMobileOnly } from 'react-device-detect';
import queryString from 'query-string';
import firebaseService from './firebaseSetup';
import { ConnectedRouter } from 'connected-react-router';
import { Provider, useSelector } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import { CookiesProvider } from 'react-cookie';
import store, { history } from 'redux/store';
import { useDispatch } from 'react-redux';
import { getUserDetailsFromToken, updateAuthState } from 'redux/reducer/auth';
import { DoconToast } from '@docon/docon-ui-library';
import Routes from './Routes';
import AgoraPlayer from 'shared/AgoraPlayer';
import { environment } from 'environments/environment';
import apolloClient from 'services/graphQLclient';
import { headerKey, restClient, storageService } from 'services';
import { Loader } from 'shared';
import { chatListFetchTag, cleverTapAnl, getClient } from 'utilities';
import { EventPermissionsAllow, EventPermissionsDeny } from 'utilities/clevertapEvent';

const BASENAME = environment.REACT_APP_BASENAME || '/';

const Toast = styled(DoconToast)`
  white-space: pre-line;
  font-size: 14px;
  line-height: 18px;
`;

function App() {
  const { user, accessToken } = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const [isMount, setIsMount] = useState(false);
  const searchParams = queryString.parse(location.search);

  useEffect(() => {
    if (accessToken) {
      setIsMount(true);
      // eslint-disable-next-line no-useless-escape
      storageService.set(storageService.user, JSON.stringify(user).replace(/\:null/gi, '\:""'));
      storageService.set(storageService.ACCESS_TOKEN, accessToken);
      restClient.setHeader(headerKey.AccessToken, accessToken);
      apolloClient.resetStore();
      if (isMobileOnly)
        getChatState();
    }
  }, [user]);

  useEffect(() => {
    if (searchParams.token) {
      const accessTokenFromUrl = searchParams.token;
      restClient.setHeader(headerKey.AccessToken, accessTokenFromUrl);
      dispatch(getUserDetailsFromToken(accessTokenFromUrl));
    }
    Promise.all([
      storageService.get(storageService.ACCESS_TOKEN),
      storageService.get(storageService.user),
    ]).then(function ([accessToken, user]) {
      if (accessToken) {
        restClient.setHeader(headerKey.AccessToken, accessToken as string);
        dispatch(updateAuthState({
          accessToken,
          user: JSON.parse(user as string),
        }));
      } else if (!searchParams.token) {
        setIsMount(true);
      }
    });
    firebaseService.initializeFirebase();
  }, []);

  function getChatState() {
    getClient(accessToken)
      .query({
        query: chatListFetchTag,
        fetchPolicy: 'no-cache',
        variables: { channel_data: true },
      })
      .then(({ data: chatData }) => {
        dispatch(updateAuthState({
          chatData,
        }));
      });
  }

  return (
    <ConnectedRouter history={history}>
      <Router basename={BASENAME}>
        <Toast
          hideProgressBar
          theme='colored'
          position='top-center'
          autoClose={4000}
        />
        <AgoraPlayer />
        {
          isMount ? <Routes /> : <Loader height='100vh' iconSize={50} />
        }
      </Router>
    </ConnectedRouter>
  );
}

function ApolloWrapper() {
  function setAppHeight() {
    (window as any).document.querySelector(':root').style
      .setProperty('--app-height', `${window.innerHeight}px`);
  }

  useLayoutEffect(() => {
    if (isMobileOnly) {
      window.addEventListener('resize', () => setAppHeight());
      setAppHeight();
    }

    function getLocalStreamPermission(params, type) {
      navigator.mediaDevices.getUserMedia(params).then(() => {
        cleverTapAnl.event({
          event_name: 'permissions_allow',
          event: {
            permission_type: type,
          },
        } as EventPermissionsAllow);
      }).catch(() => {
        cleverTapAnl.event({
          event_name: 'permissions_deny',
          event: {
            permission_type: type,
          },
        } as EventPermissionsDeny);
      });
    }

    const hasAccess = {
      audio: false,
      video: false,
    };

    if (navigator?.permissions) {
      Promise.all([
        navigator.permissions.query({ name: 'microphone' as PermissionName }),
        navigator.permissions.query({ name: 'camera' as PermissionName }),
      ]).then(function ([audio, video]) {
        if (audio.state === 'prompt' || video.state === 'prompt') {
          hasAccess.audio = audio.state === 'granted';
          hasAccess.video = video.state === 'granted';
          if (!(hasAccess.audio && hasAccess.video)) {
            let type = 'audio_video';
            switch (true) {
              case hasAccess.audio:
                type = 'video';
                break;
              case hasAccess.video:
                type = 'audio';
                break;
            }
            getLocalStreamPermission({ video: !hasAccess.video, audio: !hasAccess.audio }, type);
          }
        }
      });
    }
  }, []);

  return (
    <Provider store={store}>
      <ApolloProvider client={apolloClient}>
        <CookiesProvider>
          <App />
        </CookiesProvider>
      </ApolloProvider>
    </Provider>
  );
}

export default ApolloWrapper;